import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from 'components/urls/twitter-url.module.scss'
import cn from 'classnames'
import moment from 'moment'
import parseDomain from 'parse-domain'
import { getTweetDisplaySegments, getTweetId } from 'lib/twitterText'
import { isBlockedExternalNavigation } from 'lib/externalNavigationPolicy'
import {
    BLOCKED_EXTERNAL_NAVIGATION_CLASS,
    BLOCKED_EXTERNAL_NAVIGATION_TOAST,
} from 'components/shared/tiptap/blockedExternalNavigation'
import toast from 'utils/toast'
import 'components/shared/blocked-external-navigation.scss'
import {
    getTweetResolutionPhase,
    hasSyncedTweetData,
    isTweetResolutionComplete,
} from 'lib/twitterUrlHydration'
import * as api from 'api/api'
import * as answerCache from 'state/cache'
import MediaGrid from 'components/shared/MediaGrid'
import AppLightbox from 'components/shared/Lightbox'
import { mediaBucketsEnabled, RESERVED_URL_CARD_IMAGE_HEIGHT } from 'lib/mediaBuckets'
import {
    estimateTweetEmbedHeight,
    estimateTweetQuoteTextHeight,
    getTweetMediaGridItems,
    TWEET_EXPANDED_MAX_TEXT_LINES,
    TWEET_MAX_TEXT_LINES,
    TWEET_TEXT_LINE_PX,
} from 'lib/tweetCardHeight'
import { useRowGeometryEvents } from 'lib/rowGeometryEvents'
import { safeHttpHref } from 'lib/safeHttpHref'

function getTweetHref(url) {
    const tweetId = getTweetId(url)
    const username = url?.service_json?.author?.username || url?.service_username
    if (username && tweetId) return `https://x.com/${username}/status/${tweetId}`
    if (tweetId) return `https://x.com/i/status/${tweetId}`
    return url?.address || 'https://x.com'
}

function isTerminalTweetState(url) {
    return url?.service_status === 'twitter_missing'
}

function isLoadingTweetState(url) {
    const hasTweetReference = !!(
        url?.address ||
        url?.service_id ||
        url?.tweet_id ||
        url?.service_json?.id
    )
    return hasTweetReference && !hasSyncedTweetData(url) && !isTerminalTweetState(url)
}

function getTweetRenderKey(url) {
    const tweetId = getTweetId(url)
    if (tweetId) return `tweet:${tweetId}`
    if (url?.address) return `address:${url.address}`
    if (url?.id) return `url:${url.id}`
    return null
}

function hasTweetReference(url) {
    return !!(url?.address || url?.service_id || url?.tweet_id || url?.service_json?.id)
}

function isBlankIncomingValue(value, currentValue) {
    if (value === undefined || value === null || value === '') return true
    if (
        Array.isArray(value) &&
        value.length === 0 &&
        Array.isArray(currentValue) &&
        currentValue.length > 0
    ) {
        return true
    }
    if (
        !Array.isArray(value) &&
        typeof value === 'object' &&
        Object.keys(value).length === 0 &&
        currentValue &&
        typeof currentValue === 'object'
    ) {
        return true
    }
    return false
}

const STICKY_TWITTER_FIELDS = [
    'address',
    'service_name',
    'service_status',
    'service_id',
    'tweet_id',
    'service_json',
    'service_username',
    'avatar_url',
    'image_urls',
    'image_metadata',
    'video_urls',
    'video_thumbnail_urls',
    'video_metadata',
    'embed',
    'preview_image_url',
    'selection',
    'title',
]

function areUrlStatesEqual(leftUrl = {}, rightUrl = {}) {
    const keys = new Set([...Object.keys(leftUrl), ...Object.keys(rightUrl)])
    for (const key of keys) {
        if (leftUrl[key] !== rightUrl[key]) return false
    }
    return true
}

function mergeTwitterUrlState(currentUrl = {}, nextUrl = {}) {
    if (!hasTweetReference(nextUrl)) {
        return hasTweetReference(currentUrl) ? currentUrl : nextUrl
    }

    const currentKey = getTweetRenderKey(currentUrl)
    const nextKey = getTweetRenderKey(nextUrl)
    if (currentKey && nextKey && currentKey !== nextKey) return nextUrl

    const merged = { ...currentUrl, ...nextUrl }
    STICKY_TWITTER_FIELDS.forEach(field => {
        if (
            field === 'service_status' &&
            hasSyncedTweetData(currentUrl) &&
            nextUrl[field] === 'twitter_pending'
        ) {
            merged[field] = currentUrl[field]
            return
        }
        if (
            field === 'service_json' &&
            hasSyncedTweetData(currentUrl) &&
            !hasSyncedTweetData({ service_json: nextUrl[field] })
        ) {
            merged[field] = currentUrl[field]
            return
        }
        if (
            isBlankIncomingValue(nextUrl[field], currentUrl[field]) &&
            !isBlankIncomingValue(currentUrl[field])
        ) {
            merged[field] = currentUrl[field]
        }
    })
    return areUrlStatesEqual(currentUrl, merged) ? currentUrl : merged
}

function useStableTwitterUrl(propUrl) {
    const [url, setUrlState] = useState(() => propUrl || {})

    useEffect(() => {
        setUrlState(currentUrl => mergeTwitterUrlState(currentUrl, propUrl || {}))
    }, [propUrl])

    const setUrl = useCallback(nextUrlOrUpdater => {
        setUrlState(currentUrl => {
            const nextUrl =
                typeof nextUrlOrUpdater === 'function'
                    ? nextUrlOrUpdater(currentUrl)
                    : nextUrlOrUpdater
            return mergeTwitterUrlState(currentUrl, nextUrl || {})
        })
    }, [])

    return { url, setUrl }
}

function TwitterAvatar({ url, onError, embed = false }) {
    if (url?.avatar_url) {
        return <img src={url.avatar_url} onError={onError} />
    }

    return (
        <div className={cn(styles.avatarPlaceholder, embed && styles.embedAvatarPlaceholder)}>
            <i className="fa fa-twitter" />
        </div>
    )
}

// The card is one anchor to the post and entity links sit inside it: cancel that navigation
// (and the answer-level click) and open the entity's own destination instead. A link to a
// Treechat-lookalike domain gets the same scam block as links in post bodies (UrlPost).
const activateTweetEntityLink = (segment, ev) => {
    ev.preventDefault()
    ev.stopPropagation()
    if (isBlockedExternalNavigation(segment.href)) {
        toast.error(BLOCKED_EXTERNAL_NAVIGATION_TOAST, {
            kind: 'blocked-scam-link',
            testId: 'blocked-scam-link-toast',
        })
        return
    }
    window.open(segment.href, '_blank', 'noopener,noreferrer')
}

// Text runs keep the post's own line breaks (.selection is white-space: pre-wrap); links,
// @mentions, #hashtags and $cashtags open their X destination. They render as role="link"
// spans because an <a> cannot nest inside the card's anchor.
function TweetText({ segments }) {
    return segments.map((segment, index) => {
        if (segment.type === 'text') return segment.text

        const blocked = isBlockedExternalNavigation(segment.href)
        return (
            <span
                key={index}
                role="link"
                tabIndex={0}
                className={blocked ? BLOCKED_EXTERNAL_NAVIGATION_CLASS : styles.entityLink}
                title={
                    blocked
                        ? 'Blocked suspected scam link'
                        : segment.type === 'link'
                        ? segment.href
                        : undefined
                }
                onClick={ev => activateTweetEntityLink(segment, ev)}
                onKeyDown={ev => {
                    if (ev.key === 'Enter') activateTweetEntityLink(segment, ev)
                }}
            >
                {segment.text}
            </span>
        )
    })
}

// The post's text block: its segments plus the inline "more" toggle while folded. A post with no
// text to show (a photo-only post whose API text was just its media link) renders no block at
// all, and tweetCardHeight reserves none.
function TweetBody({ url, expanded, onShowMore, style }) {
    const { segments, isTruncated } = useMemo(
        () => getTweetDisplaySegments(url, expanded),
        [url, expanded],
    )
    if (!segments.length) return null

    return (
        <div className={styles.contentContainer} style={style}>
            <div className={styles.selection}>
                <TweetText segments={segments} />
                {isTruncated && !expanded && (
                    <>
                        {' '}
                        <span className={styles.showMore} onClick={onShowMore}>
                            more
                        </span>
                    </>
                )}
            </div>
        </div>
    )
}

function SkeletonBlock({ className }) {
    return <span className={cn(styles.skeletonBlock, className)} aria-hidden="true" />
}

function TwitterPostPlaceholder({ embed = false }) {
    return (
        <div
            className={cn(styles.skeletonPost, embed && styles.skeletonPostEmbed)}
            role="status"
            aria-label="Loading X post"
        >
            <div className={styles.skeletonHeader}>
                <SkeletonBlock className={styles.skeletonAvatar} />
                <div className={styles.skeletonIdentity}>
                    <SkeletonBlock className={styles.skeletonNameLine} />
                    <SkeletonBlock className={styles.skeletonHandleLine} />
                </div>
                {!embed && <SkeletonBlock className={styles.skeletonIconPlaceholder} />}
            </div>

            <div className={styles.skeletonContent}>
                <SkeletonBlock className={styles.skeletonTextWide} />
                <SkeletonBlock className={styles.skeletonTextMedium} />
                <SkeletonBlock className={styles.skeletonTextShort} />
            </div>

            <div
                className={styles.skeletonMediaShell}
                aria-hidden="true"
                style={
                    !embed && mediaBucketsEnabled()
                        ? {
                              height: RESERVED_URL_CARD_IMAGE_HEIGHT,
                              aspectRatio: 'auto',
                              maxHeight: 'none',
                          }
                        : undefined
                }
            >
                <SkeletonBlock className={styles.skeletonMediaPanel} />
                <SkeletonBlock className={styles.skeletonMediaAccent} />
            </div>

            <div className={styles.skeletonFooter} aria-hidden="true">
                <SkeletonBlock className={styles.skeletonFooterDot} />
                <SkeletonBlock className={styles.skeletonFooterLine} />
            </div>
        </div>
    )
}

export default function TwitterUrl(props) {
    const { url, setUrl } = useStableTwitterUrl(props.url)
    const rowGeometryEvents = useRowGeometryEvents()
    const hasRetriedMediaRef = useRef(false)
    const hasNotifiedReadyRef = useRef(false)
    const resolveStateRef = useRef({ key: null, attempts: 0, timeout: null })
    const [resolveTick, setResolveTick] = useState(0)
    const [isExpanded, setIsExpanded] = useState(false)
    const [lightboxOpen, setLightboxOpen] = useState(false)
    const [lightboxSlides, setLightboxSlides] = useState([])
    const [lightboxIndex, setLightboxIndex] = useState(0)

    const tweetRenderKey = getTweetRenderKey(url)
    const tweetResolutionPhase = getTweetResolutionPhase(url)
    const cacheResolvedUrlForAnswer = useCallback(
        nextUrl => {
            if (!props.answerId || !hasTweetReference(nextUrl)) return
            answerCache.updateCachedAnswer(props.answerId, { url: nextUrl })
        },
        [props.answerId],
    )
    const notifyReady = useCallback(() => {
        if (hasNotifiedReadyRef.current) return

        hasNotifiedReadyRef.current = true
        props.onReady?.()
    }, [props.onReady])

    useEffect(() => {
        hasRetriedMediaRef.current = false
        hasNotifiedReadyRef.current = false
    }, [tweetRenderKey])

    useEffect(() => {
        return () => {
            if (resolveStateRef.current.timeout) {
                clearTimeout(resolveStateRef.current.timeout)
            }
        }
    }, [])

    useEffect(() => {
        const resolveAddress = url?.address || (getTweetId(url) ? getTweetHref(url) : null)
        if (!resolveAddress) return
        if (hasSyncedTweetData(url)) notifyReady()
        if (isTweetResolutionComplete(url)) {
            return
        }
        if (isTerminalTweetState(url)) return

        const resolveKey = `${url.id || ''}:${resolveAddress}:${tweetResolutionPhase}`
        if (resolveStateRef.current.key !== resolveKey) {
            if (resolveStateRef.current.timeout) {
                clearTimeout(resolveStateRef.current.timeout)
            }
            resolveStateRef.current = { key: resolveKey, attempts: 0, timeout: null }
        }

        const attempt = resolveStateRef.current.attempts
        if (attempt >= 8) return
        resolveStateRef.current.attempts = attempt + 1

        const scheduleRetry = nextUrl => {
            if (isTweetResolutionComplete(nextUrl) || isTerminalTweetState(nextUrl)) return

            const delay = Math.min(1000 * 2 ** attempt, 8000)
            if (resolveStateRef.current.timeout) {
                clearTimeout(resolveStateRef.current.timeout)
            }
            resolveStateRef.current.timeout = setTimeout(() => {
                setResolveTick(tick => tick + 1)
            }, delay)
        }

        api.resolveUrl(resolveAddress)
            .then(resolved => {
                const nextUrl =
                    resolved?.ok && resolved.url ? mergeTwitterUrlState(url, resolved.url) : url
                if (resolved?.ok && resolved.url) {
                    setUrl(curr => mergeTwitterUrlState(curr, nextUrl))
                    cacheResolvedUrlForAnswer(nextUrl)
                    if (hasSyncedTweetData(nextUrl)) notifyReady()
                }

                scheduleRetry(nextUrl)
            })
            .catch(() => {
                scheduleRetry(url)
            })
    }, [
        tweetRenderKey,
        url?.address,
        url?.id,
        url?.service_status,
        url?.service_json?.id,
        tweetResolutionPhase,
        resolveTick,
        cacheResolvedUrlForAnswer,
        notifyReady,
    ])

    const handleMediaError = async () => {
        if (hasRetriedMediaRef.current || !url?.id) return
        hasRetriedMediaRef.current = true

        const refreshed = await api.refreshTweetEmbedMedia(url.id)
        if (refreshed?.ok && refreshed.url) {
            const nextUrl = mergeTwitterUrlState(url, refreshed.url)
            setUrl(nextUrl)
            cacheResolvedUrlForAnswer(nextUrl)
        }
    }

    const handleShowMore = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        setIsExpanded(true)
        // Under static geometry the row's reserved height is frozen per content signature, so
        // this in-place growth must be reported for a re-estimate with the full text; without a
        // provider (non-virtualized surfaces) local expansion behaves exactly as before.
        rowGeometryEvents?.notifyTweetExpandedChange({ answerId: props.answerId, expanded: true })
    }

    // Under a provider the expansion is CONTROLLED by the row's geometry state: local
    // component state dies on remount (v1 key rotation remounts the card synchronously on the
    // very click that expands it; v2 rows remount after leaving the virtual window), and a
    // collapsed render inside an expanded reserve is a permanent blank gap. Reading the same
    // state the estimator reserves from keeps render and reserve in lockstep across remounts.
    const contextTweetExpanded = rowGeometryEvents?.isTweetExpanded?.(props.answerId)
    const effectiveIsExpanded =
        contextTweetExpanded == null ? isExpanded : contextTweetExpanded || isExpanded

    const isLoadingTweet = isLoadingTweetState(url)

    const mediaItems = useMemo(() => getTweetMediaGridItems(url), [url])
    const mediaPending = mediaItems[0]?.type === 'pending'
    const quoteGeometryPinned = mediaBucketsEnabled() && Number(props.estimatedWidth || 0) > 0
    // Pin the quote wrapper to the exact text/media projection reserved by answer geometry.
    // Keeping this width input aligned with estimateTweetCardProjectionHeight prevents both
    // nested clipping and a mounted-vs-placeholder height swing.
    const quoteEmbedHeight = quoteGeometryPinned
        ? estimateTweetEmbedHeight(url, {
              width: props.estimatedWidth,
              expanded: effectiveIsExpanded,
          })
        : 0
    const quoteTextHeight = quoteGeometryPinned
        ? estimateTweetQuoteTextHeight(url.embed, {
              width: props.estimatedWidth,
              expanded: effectiveIsExpanded,
          })
        : 0
    const handleQuoteUrlChange = useCallback(
        nextEmbed => {
            const nextUrl = mergeTwitterUrlState(url, { ...url, embed: nextEmbed })
            setUrl(nextUrl)
            cacheResolvedUrlForAnswer(nextUrl)
        },
        [cacheResolvedUrlForAnswer, setUrl, url],
    )

    const handleImageClick = index => {
        // Get only image URLs for lightbox
        const imageUrls = mediaItems.filter(item => item.type === 'image').map(item => item.url)

        // Calculate which image was clicked (accounting for videos before it)
        let imageIndex = 0
        let imageCount = 0
        for (let i = 0; i <= index; i++) {
            if (mediaItems[i].type === 'image') {
                imageIndex = imageCount
                imageCount++
            }
        }

        setLightboxSlides(imageUrls.map(imgUrl => ({ src: imgUrl })))
        setLightboxIndex(imageIndex)
        setLightboxOpen(true)
    }

    return (
        <>
            <a
                className={styles.twitterUrlComp}
                onClick={ev => ev.stopPropagation()}
                href={getTweetHref(url)}
                target="_blank"
                rel="noopener noreferrer"
                aria-busy={isLoadingTweet ? true : undefined}
            >
                {isLoadingTweet ? (
                    <TwitterPostPlaceholder />
                ) : (
                    <>
                        <div className={styles.userHeader}>
                            <div className={styles.avatar}>
                                <TwitterAvatar url={url} onError={handleMediaError} />
                            </div>

                            <div className={styles.nameContainer}>
                                <div className={styles.screenName}>
                                    {url.service_json?.author?.name || 'X post'}
                                </div>

                                <div className={styles.name}>
                                    {url.service_json?.author?.username
                                        ? `@${url.service_json.author.username}`
                                        : getTweetId(url)
                                        ? `#${getTweetId(url)}`
                                        : 'x.com'}
                                </div>
                            </div>

                            <div className={styles.twitterIcon}>
                                <i className="fa fa-twitter" />
                            </div>
                        </div>

                        <TweetBody
                            url={url}
                            expanded={effectiveIsExpanded}
                            onShowMore={handleShowMore}
                            // The estimate saturates at TWEET_MAX_TEXT_LINES collapsed and
                            // TWEET_EXPANDED_MAX_TEXT_LINES expanded; pinning the rendered text
                            // to the same window (internal scroll) keeps pathological posts —
                            // long notes, or 280 chars of one-word lines — fully readable inside
                            // the frozen row reserve instead of hard-clipped by the static row
                            // frame. overflow-x stays hidden so no horizontal scrollbar can add
                            // height the estimate never reserved.
                            style={
                                mediaBucketsEnabled()
                                    ? {
                                          maxHeight:
                                              (effectiveIsExpanded
                                                  ? TWEET_EXPANDED_MAX_TEXT_LINES
                                                  : TWEET_MAX_TEXT_LINES) * TWEET_TEXT_LINE_PX,
                                          overflowY: 'auto',
                                          overflowX: 'hidden',
                                      }
                                    : undefined
                            }
                        />

                        {mediaItems.length > 0 && (
                            <div className={styles.mediaContainer}>
                                <MediaGrid
                                    items={mediaItems}
                                    answerId={props.answerId}
                                    onImageClick={handleImageClick}
                                    onMediaError={handleMediaError}
                                    showMediaActionsMenu={!mediaPending}
                                    matchingBsvAttachment={props.matchingBsvAttachment}
                                />
                            </div>
                        )}

                        {url.embed && url.embed.service_name === 'twitter' && (
                            <div
                                className={cn(
                                    styles.urlEmbedContainer,
                                    // Flag-gated: pin the variable-height quote card to the exact
                                    // projected window reserved by tweetCardHeight.
                                    quoteGeometryPinned && styles.quoteEmbedWindow,
                                )}
                                style={
                                    quoteGeometryPinned && quoteEmbedHeight > 0
                                        ? { height: quoteEmbedHeight }
                                        : undefined
                                }
                            >
                                <TwitterUrlEmbed
                                    url={url.embed}
                                    answerId={props.answerId}
                                    expanded={effectiveIsExpanded}
                                    onShowMore={handleShowMore}
                                    textHeight={quoteTextHeight}
                                    geometryPinned={quoteGeometryPinned}
                                    onUrlChange={handleQuoteUrlChange}
                                />
                            </div>
                        )}

                        {url.embed && url.embed.service_name === 'website' && (
                            <div className={styles.urlEmbedContainer}>
                                <WebsiteUrlEmbed url={url.embed} />
                            </div>
                        )}

                        {url.service_json?.created_at && (
                            <div className={styles.dateTimeContainer}>
                                {moment(url.service_json.created_at).format('LT · LL')}
                            </div>
                        )}
                    </>
                )}
            </a>

            <AppLightbox
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                slides={lightboxSlides}
                index={lightboxIndex}
            />
        </>
    )
}

function TwitterUrlEmbed(props) {
    const { url, setUrl } = useStableTwitterUrl(props.url)
    const hasRetriedMediaRef = useRef(false)
    const [lightboxOpen, setLightboxOpen] = useState(false)
    const [lightboxSlides, setLightboxSlides] = useState([])
    const [lightboxIndex, setLightboxIndex] = useState(0)

    const tweetRenderKey = getTweetRenderKey(url)

    useEffect(() => {
        hasRetriedMediaRef.current = false
    }, [tweetRenderKey])

    const handleMediaError = async () => {
        if (hasRetriedMediaRef.current || !url?.id) return
        hasRetriedMediaRef.current = true

        const refreshed = await api.refreshTweetEmbedMedia(url.id)
        if (refreshed?.ok && refreshed.url) {
            const nextUrl = mergeTwitterUrlState(url, refreshed.url)
            setUrl(nextUrl)
            props.onUrlChange?.(nextUrl)
        }
    }

    const isLoadingTweet = isLoadingTweetState(url)

    const mediaItems = useMemo(() => getTweetMediaGridItems(url), [url])
    const mediaPending = mediaItems[0]?.type === 'pending'

    const handleImageClick = index => {
        // Get only image URLs for lightbox
        const imageUrls = mediaItems.filter(item => item.type === 'image').map(item => item.url)

        // Calculate which image was clicked (accounting for videos before it)
        let imageIndex = 0
        let imageCount = 0
        for (let i = 0; i <= index; i++) {
            if (mediaItems[i].type === 'image') {
                imageIndex = imageCount
                imageCount++
            }
        }

        setLightboxSlides(imageUrls.map(imgUrl => ({ src: imgUrl })))
        setLightboxIndex(imageIndex)
        setLightboxOpen(true)
    }

    return (
        <>
            <div
                className={styles.twitterUrlEmbedComp}
                aria-busy={isLoadingTweet ? true : undefined}
            >
                {isLoadingTweet ? (
                    <TwitterPostPlaceholder embed />
                ) : (
                    <>
                        <div className={styles.embedUserHeader}>
                            <div className={cn(styles.avatar, styles.embedAvatar)}>
                                <TwitterAvatar url={url} onError={handleMediaError} embed />
                            </div>

                            <div className={cn(styles.screenName, styles.embedScreenName)}>
                                {url.service_json?.author?.name || 'X post'}
                            </div>

                            <div className={cn(styles.name, styles.embedName)}>
                                {url.service_json?.author?.username
                                    ? `@${url.service_json.author.username}`
                                    : getTweetId(url)
                                    ? `#${getTweetId(url)}`
                                    : 'x.com'}
                            </div>
                        </div>

                        <TweetBody
                            url={url}
                            // The host card owns the expansion state: its "more" (and the row's
                            // tweet-expanded flag it reports to) opens the quoted text too, and
                            // the quote's own "more" goes through the same handler so the pinned
                            // quote window and the row reserve grow with the text instead of the
                            // text scrolling inside a frozen window.
                            expanded={!!props.expanded}
                            onShowMore={props.onShowMore}
                            // The projected text slot is the render contract as well as the
                            // reservation. Overflow remains readable inside the slot if actual
                            // glyph wrapping differs from the deterministic character estimate.
                            style={
                                props.geometryPinned && props.textHeight > 0
                                    ? {
                                          height: props.textHeight,
                                          overflowY: 'auto',
                                          overflowX: 'hidden',
                                      }
                                    : undefined
                            }
                        />

                        {mediaItems.length > 0 && (
                            <div className={styles.embedMediaContainer}>
                                <MediaGrid
                                    items={mediaItems}
                                    answerId={props.answerId}
                                    onImageClick={handleImageClick}
                                    onMediaError={handleMediaError}
                                    showMediaActionsMenu={!mediaPending}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            <AppLightbox
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                slides={lightboxSlides}
                index={lightboxIndex}
            />
        </>
    )
}

function WebsiteUrlEmbed(props) {
    const url = props.url
    const imageUrls = url.image_urls
    const imgUrl = imageUrls.length ? imageUrls[imageUrls.length - 1] : null
    const displayUrl = url.service_json['display_url']

    const address = url.service_json['unwound_url']

    const navigateToUrl = () => {
        const href = safeHttpHref(address)
        if (href) window.open(href, '_blank', 'noopener,noreferrer')
    }

    const parse = parseDomain(address)
    let domain,
        img = null
    if (parse) {
        domain = `${parse.domain}.${parse.tld}`
        img = `https://icon.horse/icon/${domain}?size=small`
    }

    return (
        <div className={styles.webEmbedComp} onClick={navigateToUrl}>
            {imgUrl ? (
                <div
                    className={styles.webImageContainer}
                    style={{
                        backgroundImage: `url("${imgUrl}")`,
                    }}
                ></div>
            ) : (
                <div className={styles.webImagePlaceholder}>
                    <img src={img} />
                </div>
            )}

            <div className={styles.webContentContainer}>
                <div className={styles.displayUrl}>{displayUrl}</div>

                <div className={styles.title}>{url.title}</div>

                <div className={styles.description}>{url.selection}</div>
            </div>
        </div>
    )
}
